"use client";

/**
 * DataShareTool — paste a share link or drop an export, see exactly what would be shared and what
 * the checks found.
 *
 * Two inputs, one result view:
 *   - Share link: POST /api/data-share/fetch (the server fetches the public link, it never stores it).
 *   - Export file: read and checked ENTIRELY in this browser tab (lib/data-share/exports.ts).
 *
 * Two passes, both run here in the browser on whatever came back:
 *   1. Safety (lib/data-share/analyse.ts): prompt injection, hidden text, harmful code, unsafe links.
 *      Decides passed / needs a look / refused.
 *   2. Privacy (lib/data-share/redact.ts, after DataClaw): usernames to stable hashes, keys and tokens,
 *      random-looking strings, secret-named fields, emails, phones, cards, IBANs, the visitor's own
 *      words. Never refuses anything: it REMOVES, and reports what it removed.
 *
 * The default view is "What you'd share" (the redacted copy); "Original" shows the untouched text
 * and is only ever on this screen. The download is the redacted copy in the donation format.
 *
 * PREVIEW ONLY (2026-11-04): nothing is saved and there is no donate button yet. The copy says so.
 *
 * Shared text is shown as plain text, never as HTML or rendered markdown, and images are never loaded.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  AudioLines,
  Bot,
  Brain,
  ChevronDown,
  CircleCheck,
  CircleX,
  ExternalLink,
  EyeOff,
  FileArchive,
  FileText,
  ImageIcon,
  Link2,
  Loader2,
  Lock,
  Mic,
  MousePointerClick,
  Paperclip,
  Search,
  ShieldCheck,
  TriangleAlert,
  Upload,
  User,
  Wrench,
} from "lucide-react";
import { analyseConversation } from "@/lib/data-share/analyse";
import { BUTTON_ORIGINS, READY_MESSAGE, bookmarkletHref, readButtonMessage } from "@/lib/data-share/bookmarklet";
import { readExportFile } from "@/lib/data-share/exports";
import { ButtonGuide } from "@/components/marketing/data-share-button-guide";
import { DonatePanel, type DonationState, type Donated } from "@/components/marketing/data-share-donate";
import { FaviconTile } from "@/components/marketing/favicon-tile";
import { PROVIDER_DOMAIN, PROVIDER_LABEL, parseShareLink } from "@/lib/data-share/providers";
import {
  PRIVACY_LABEL,
  isIncluded,
  redactConversation,
  toShareRecord,
  type PrivacyReport,
  type PrivacyType,
  type RedactOptions,
} from "@/lib/data-share/redact";
import type {
  AnalysedConversation,
  ConversationAnalysis,
  Finding,
  ShareAttachment,
  ShareConversation,
  ShareMessage,
  ShareProvider,
  Verdict,
} from "@/lib/data-share/types";

const ENDPOINT = "/api/data-share/fetch";
const DONATE_ENDPOINT = "/api/marketing/data-share/donate";
/** Must match CONSENT_VERSION in shared_backend/apps/marketing/api/data_share.py. */
const CONSENT_VERSION = "2026-11-05";
const LIST_CAP = 300;
const MESSAGE_PREVIEW_CHARS = 1_800;

const VERDICT: Record<Verdict, { label: string; icon: typeof CircleCheck; box: string; text: string }> = {
  clean: { label: "Passed the safety check", icon: CircleCheck, box: "border-emerald-500/30 bg-emerald-500/10", text: "text-emerald-700 dark:text-emerald-400" },
  review: { label: "Needs a closer look", icon: TriangleAlert, box: "border-amber-500/30 bg-amber-500/10", text: "text-amber-700 dark:text-amber-400" },
  rejected: { label: "Refused", icon: CircleX, box: "border-red-500/30 bg-red-500/10", text: "text-red-700 dark:text-red-400" },
};

const SEVERITY_DOT: Record<Finding["severity"], string> = {
  block: "bg-red-500",
  review: "bg-amber-500",
  info: "bg-foreground/30",
};

const CATEGORY_LABEL: Record<Finding["category"], string> = {
  prompt_injection: "Prompt injection",
  hidden_text: "Hidden text",
  malicious_code: "Harmful code",
  unsafe_link: "Unsafe link",
  secret: "Key or password",
  personal_data: "Personal details",
};

const tick = () => new Promise<void>((r) => setTimeout(r, 0));

function splitList(s: string): string[] {
  return s.split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
}

function fmtBytes(n?: number): string {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 ** 2).toFixed(1)} MB`;
}

function fmtTime(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/* ───────────────────────────── small parts ───────────────────────────── */

function VerdictPill({ v, small }: { v: Verdict; small?: boolean }) {
  const s = VERDICT[v];
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border ${s.box} ${s.text} ${small ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs"} font-medium whitespace-nowrap`}>
      <Icon className={small ? "h-3 w-3" : "h-3.5 w-3.5"} />
      {s.label}
    </span>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border hairline bg-background/60 px-3 py-2.5">
      <div className="font-mono text-lg leading-none">{typeof value === "number" ? value.toLocaleString() : value}</div>
      <div className="mt-1.5 text-[11px] uppercase tracking-[0.12em] text-foreground/50">{label}</div>
    </div>
  );
}

/** Plain text with redaction tokens picked out, so the visitor can see every removal. */
const TOKEN = /(\[REDACTED:[a-z]+\]|\buser_[0-9a-f]{8}\b)/g;
function RedactedText({ text, highlight }: { text: string; highlight: boolean }) {
  if (!highlight) return <>{text}</>;
  const parts = text.split(TOKEN);
  return (
    <>
      {parts.map((p, i) =>
        i % 2 === 1 ? (
          <span key={i} className="rounded bg-[hsl(var(--accent)/0.15)] px-1 font-mono text-[0.85em] text-[hsl(var(--accent))]">
            {p}
          </span>
        ) : (
          <Fragment key={i}>{p}</Fragment>
        ),
      )}
    </>
  );
}

function AttachmentChip({ a }: { a: ShareAttachment }) {
  const Icon = a.kind === "image" ? ImageIcon : a.kind === "audio" ? AudioLines : Paperclip;
  const dims = a.width && a.height ? `${a.width}×${a.height}` : "";
  const extra = [dims, fmtBytes(a.bytes)].filter(Boolean).join(" · ");
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-lg border hairline bg-background/70 px-2 py-1 text-[11.5px] text-foreground/70">
      <Icon className="h-3.5 w-3.5 shrink-0 text-foreground/45" />
      <span className="truncate">{a.name}</span>
      {extra && <span className="shrink-0 font-mono text-[10.5px] text-foreground/40">{extra}</span>}
    </span>
  );
}

function MessageRow({
  m,
  index,
  flagged,
  included,
  highlight,
}: {
  m: ShareMessage;
  index: number;
  flagged: Finding[];
  included: boolean;
  highlight: boolean;
}) {
  const [open, setOpen] = useState(false);
  const collapsible = m.kind === "thinking" || m.kind === "tool_call" || m.kind === "tool_result" || m.kind === "document";
  const [expanded, setExpanded] = useState(!collapsible);
  const long = m.text.length > MESSAGE_PREVIEW_CHARS;
  const text = open || !long ? m.text : `${m.text.slice(0, MESSAGE_PREVIEW_CHARS)}…`;

  const who =
    m.kind === "thinking"
      ? { icon: Brain, label: "Reasoning" }
      : m.kind === "tool_call"
        ? { icon: Wrench, label: `Tool call${m.name ? ` · ${m.name}` : ""}` }
        : m.kind === "tool_result"
          ? { icon: Wrench, label: `Tool result${m.name ? ` · ${m.name}` : ""}${m.isError ? " · failed" : ""}` }
          : m.kind === "memory" || m.kind === "document"
            ? { icon: FileText, label: m.name || "Document" }
            : m.role === "user"
              ? { icon: User, label: "You" }
              : { icon: Bot, label: "Assistant" };
  const Icon = who.icon;
  const worst = flagged.find((f) => f.severity === "block") || flagged.find((f) => f.severity === "review") || flagged[0];

  if (!included) {
    return (
      <li id={`msg-${index}`} className="flex items-center gap-2 rounded-xl border border-dashed hairline px-4 py-2 text-[12px] text-foreground/45">
        <EyeOff className="h-3.5 w-3.5" />
        {who.label} left out
        <span className="ml-auto font-mono text-[10px]">#{index + 1}</span>
      </li>
    );
  }

  return (
    <li
      id={`msg-${index}`}
      className={`rounded-xl border px-4 py-3 ${m.role === "user" && m.kind === "text" ? "hairline-strong bg-foreground/[0.03]" : "hairline bg-background/50"} ${
        worst ? (worst.severity === "block" ? "ring-1 ring-red-500/40" : worst.severity === "review" ? "ring-1 ring-amber-500/40" : "") : ""
      }`}
    >
      <button type="button" onClick={() => collapsible && setExpanded((x) => !x)} className={`flex w-full items-center gap-2 text-left ${collapsible ? "cursor-pointer" : "cursor-default"}`}>
        <Icon className="h-3.5 w-3.5 shrink-0 text-foreground/50" />
        <span className="text-[12px] font-medium text-foreground/70 truncate">{who.label}</span>
        {m.voice && (
          <span className="inline-flex items-center gap-1 rounded-full border hairline px-1.5 py-0.5 text-[10.5px] text-foreground/55">
            <Mic className="h-3 w-3" /> voice
          </span>
        )}
        {m.model && <span className="hidden sm:inline font-mono text-[10.5px] text-foreground/40">{m.model}</span>}
        {flagged.length > 0 && (
          <span className="ml-1 inline-flex items-center gap-1 text-[11px] text-foreground/55">
            <span className={`h-1.5 w-1.5 rounded-full ${SEVERITY_DOT[worst!.severity]}`} />
            {flagged.length} flag{flagged.length === 1 ? "" : "s"}
          </span>
        )}
        <span className="ml-auto shrink-0 font-mono text-[10px] text-foreground/35" title={m.at ? fmtTime(m.at) : undefined}>
          #{index + 1}
        </span>
        {collapsible && <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-foreground/40 transition-transform ${expanded ? "rotate-180" : ""}`} />}
      </button>
      {expanded && (
        <>
          {m.text && (
            <div
              className={`mt-2 whitespace-pre-wrap break-words text-[13.5px] leading-relaxed ${
                m.kind === "tool_call" || m.kind === "tool_result" ? "font-mono text-[12px] text-foreground/70" : "text-foreground/85"
              } ${m.kind === "thinking" ? "italic text-foreground/65" : ""}`}
            >
              <RedactedText text={text} highlight={highlight} />
            </div>
          )}
          {long && (
            <button type="button" onClick={() => setOpen((x) => !x)} className="mt-2 text-[12px] text-foreground/55 underline underline-offset-2 hover:text-foreground">
              {open ? "Show less" : `Show all ${m.text.length.toLocaleString()} characters`}
            </button>
          )}
          {!!m.attachments?.length && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {m.attachments.map((a, i) => (
                <AttachmentChip key={i} a={a} />
              ))}
            </div>
          )}
        </>
      )}
    </li>
  );
}

function FindingsList({ a }: { a: ConversationAnalysis }) {
  if (!a.findings.length) {
    return (
      <p className="flex items-center gap-2 text-[13px] text-foreground/65">
        <ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        No prompt injection, hidden text, harmful code or unsafe links found.
      </p>
    );
  }
  return (
    <ul className="space-y-2">
      {a.findings.map((f, i) => (
        <li key={i} className="rounded-lg border hairline bg-background/60 px-3 py-2">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={`h-2 w-2 rounded-full ${SEVERITY_DOT[f.severity]}`} />
            <span className="text-[12px] font-medium">{CATEGORY_LABEL[f.category]}</span>
            <span className="text-[12px] text-foreground/60">· {f.label}</span>
            <a href={`#msg-${f.message}`} className="ml-auto font-mono text-[11px] text-foreground/45 hover:text-foreground">
              {f.role} message #{f.message + 1}
            </a>
          </div>
          <p className="mt-1 font-mono text-[11.5px] text-foreground/55 break-all">{f.excerpt}</p>
        </li>
      ))}
      {a.moreFindings > 0 && <li className="text-[12px] text-foreground/55">and {a.moreFindings} more.</li>}
    </ul>
  );
}

function PrivacyPanel({ r }: { r: PrivacyReport }) {
  if (!r.total) {
    return (
      <p className="flex items-center gap-2 text-[13px] text-foreground/65">
        <Lock className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        No usernames, keys, passwords, emails, phone numbers, card or bank numbers found.
      </p>
    );
  }
  const types = Object.entries(r.byType).filter(([, n]) => n) as [PrivacyType, number][];
  return (
    <div className="space-y-3">
      <p className="text-[13px] text-foreground/70">
        <span className="font-medium text-foreground">{r.total.toLocaleString()}</span> private item{r.total === 1 ? "" : "s"} found. Each is
        replaced in the copy you would share; nothing private goes with it.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {types.map(([t, n]) => (
          <span key={t} className="inline-flex items-center gap-1.5 rounded-full border hairline bg-background/70 px-2.5 py-1 text-[12px]">
            {PRIVACY_LABEL[t]} <span className="font-mono text-foreground/55">{n}</span>
          </span>
        ))}
      </div>
      {r.detectedUsernames.length > 0 && (
        <p className="text-[12px] text-foreground/55">
          Found in file paths and hidden as a stable code: {r.detectedUsernames.length} username{r.detectedUsernames.length === 1 ? "" : "s"}.
        </p>
      )}
      <ul className="max-h-[220px] overflow-y-auto space-y-1">
        {r.items.map((it, i) => (
          <li key={i} className="flex items-center gap-2 text-[12px]">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--accent))]" />
            <span className="text-foreground/70">{it.label}</span>
            <span className="font-mono text-foreground/45 truncate">{it.masked}</span>
            {it.message >= 0 ? (
              <a href={`#msg-${it.message}`} className="ml-auto shrink-0 font-mono text-[11px] text-foreground/45 hover:text-foreground">
                #{it.message + 1}
              </a>
            ) : (
              <span className="ml-auto shrink-0 font-mono text-[11px] text-foreground/45">title</span>
            )}
          </li>
        ))}
        {r.total > r.items.length && <li className="text-[12px] text-foreground/50">and {(r.total - r.items.length).toLocaleString()} more.</li>}
      </ul>
    </div>
  );
}

/** What kinds of data this conversation carries, and whether each goes into the donation. */
function IncludedPanel({ c, a, opts }: { c: ShareConversation; a: ConversationAnalysis; opts: RedactOptions }) {
  const s = a.stats;
  const metaKeys = [
    c.model && "model",
    (c.createdAt || c.messages.some((m) => m.at)) && "timestamps",
    ...Object.keys(c.meta || {}),
  ].filter(Boolean) as string[];
  const rows: { label: string; value: string; on: boolean }[] = [
    { label: "Your messages", value: String(s.userMessages), on: true },
    { label: "Voice transcripts", value: String(s.voiceMessages), on: true },
    { label: "Images and files", value: s.images + s.files ? `${s.images} images, ${s.files} files (details only)` : "0", on: true },
    { label: "Assistant replies", value: String(s.assistantMessages), on: true },
    { label: "Reasoning", value: String(s.thinkingBlocks), on: opts.includeThinking !== false },
    { label: "Tool calls and results", value: String(s.toolMessages), on: opts.includeTools !== false },
    {
      label: "Token usage",
      value: !c.usage
        ? "not recorded by this source"
        : c.usage.inputTokens != null || c.usage.outputTokens != null
          ? `${c.usage.inputTokens?.toLocaleString() ?? "?"} in / ${c.usage.outputTokens?.toLocaleString() ?? "?"} out`
          : `${(c.usage.totalTokens ?? 0).toLocaleString()} in total`,
      on: !!c.usage,
    },
    { label: "Details", value: metaKeys.length ? metaKeys.join(", ") : "none", on: metaKeys.length > 0 },
  ];
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
      {rows.map((r) => (
        <li key={r.label} className="flex items-center gap-2 text-[12.5px]">
          {r.on ? <CircleCheck className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" /> : <EyeOff className="h-3.5 w-3.5 shrink-0 text-foreground/35" />}
          <span className="text-foreground/75">{r.label}</span>
          <span className="ml-auto truncate font-mono text-[11px] text-foreground/50">{r.value}</span>
        </li>
      ))}
    </ul>
  );
}

function ConversationView({ item, opts }: { item: AnalysedConversation; opts: RedactOptions }) {
  const { conversation: original, analysis: a } = item;
  const [view, setView] = useState<"share" | "original">("share");
  const { conversation: redacted, report } = useMemo(() => redactConversation(original, opts), [original, opts]);
  const c = view === "share" ? redacted : original;
  const s = a.stats;
  const byMessage = useMemo(() => {
    const m = new Map<number, Finding[]>();
    for (const f of a.findings) m.set(f.message, [...(m.get(f.message) || []), f]);
    return m;
  }, [a.findings]);
  const isDocs = c.source === "memory" || c.source === "project";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.16em] text-foreground/45">
            {PROVIDER_LABEL[c.provider]}
            {c.model ? ` · ${c.model}` : ""}
            {c.createdAt ? ` · ${new Date(c.createdAt).toLocaleDateString()}` : ""}
          </div>
          <h3 className="mt-1 display-serif text-xl leading-snug break-words">{c.title}</h3>
        </div>
        {original.url && (
          <a href={original.url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1 rounded-full border hairline px-3 py-1 text-[12px] text-foreground/65 hover:text-foreground">
            Original <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {isDocs ? (
          <>
            <Stat label="Notes" value={c.messages.length} />
            <Stat label="Words" value={s.words} />
            <Stat label="Private items" value={report.total} />
            <Stat label="Usefulness" value={`${s.quality}/100`} />
          </>
        ) : (
          <>
            <Stat label="Turns" value={s.turns} />
            <Stat label="Words" value={s.words} />
            <Stat label="Private items" value={report.total} />
            <Stat label="Usefulness" value={`${s.quality}/100`} />
          </>
        )}
      </div>
      {s.qualityNotes.length > 0 && <p className="-mt-3 text-[12px] text-foreground/55">{s.qualityNotes.join(" · ")}</p>}

      {/* Analysis: safety first, then privacy. */}
      <div className="grid grid-cols-1 gap-4">
        <section className={`rounded-xl border px-4 py-4 ${VERDICT[a.verdict].box}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="kicker">Safety</div>
            <VerdictPill v={a.verdict} />
          </div>
          {a.verdict !== "clean" && <p className="mt-2 text-[13px] text-foreground/75">{a.reason}</p>}
          <div className="mt-3">
            <FindingsList a={a} />
          </div>
        </section>
        <section className="rounded-xl border hairline bg-background/50 px-4 py-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="kicker">Privacy</div>
            <span className="inline-flex items-center gap-1 text-[12px] text-foreground/55">
              <Lock className="h-3.5 w-3.5" /> removed before sharing
            </span>
          </div>
          <div className="mt-3">
            <PrivacyPanel r={report} />
          </div>
        </section>
        <section className="rounded-xl border hairline bg-background/50 px-4 py-4">
          <div className="kicker">What goes with it</div>
          <div className="mt-3">
            <IncludedPanel c={original} a={a} opts={opts} />
          </div>
        </section>
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="kicker">{isDocs ? "The notes" : "The conversation"}</div>
          <div role="tablist" className="inline-flex rounded-full border hairline p-0.5">
            {(
              [
                ["share", "What you'd share"],
                ["original", "Original"],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                role="tab"
                aria-selected={view === k}
                type="button"
                onClick={() => setView(k)}
                className={`rounded-full px-3 py-1 text-[12px] transition-colors ${view === k ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <p className="mt-2 text-[12px] text-foreground/50">
          {view === "share"
            ? "This is the copy that would be donated, with private details replaced and highlighted. Read it through: the automatic removal can miss things."
            : "The untouched conversation. Only you can see this view; it is never shared."}
        </p>
        <ul className="mt-3 space-y-2 max-h-[640px] overflow-y-auto pr-1">
          {c.messages.map((m, i) => (
            <MessageRow
              key={`${view}-${i}`}
              m={m}
              index={i}
              flagged={byMessage.get(i) || []}
              included={view === "original" || isIncluded(original.messages[i], opts)}
              highlight={view === "share"}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ───────────────────────────── the tool ───────────────────────────── */

/** Provider cards under the link box, in this order. `soon` = recognised but not readable yet. */
const HOW: { p: ShareProvider; how: string; soon?: boolean }[] = [
  { p: "chatgpt", how: "Open the chat, press Share, then Create link and copy it." },
  { p: "claude", how: "Open the chat, press Share, then Create public link and copy it." },
  { p: "gemini", how: "Open the chat, press Share (or the ⋮ menu), then Create public link and copy it." },
  { p: "deepseek", how: "Open the chat, press Share, choose the messages, then Create link and copy it." },
  { p: "grok", how: "Open the chat, press Share, then Create link and copy it." },
  { p: "kimi", how: "Open the chat, press Share and paste what it copies. The whole sentence is fine." },
  { p: "mistral", how: "Press Share and make it public. Paste the link, or open it and click the askFinz button." },
  { p: "perplexity", how: "Press Share and copy the link. Open it and click the askFinz button." },
  { p: "zai", how: "Press Share and copy the link. Open it and click the askFinz button." },
];

export function DataShareTool() {
  const [tab, setTab] = useState<"link" | "file">("link");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const [items, setItems] = useState<AnalysedConversation[]>([]);
  const [notes, setNotes] = useState<string[]>([]);
  const [selected, setSelected] = useState(0);
  const [filter, setFilter] = useState("");
  const [verdictFilter, setVerdictFilter] = useState<Verdict | "all">("all");
  const [dragging, setDragging] = useState(false);
  const [buttonHint, setButtonHint] = useState(false);
  const [excluded, setExcluded] = useState<Set<number>>(() => new Set());
  const [consent, setConsent] = useState(false);
  const [donation, setDonation] = useState<DonationState>({ status: "idle", done: [], refused: [], error: "" });
  const closeGuide = useCallback(() => setButtonHint(false), []);
  const fileRef = useRef<HTMLInputElement>(null);

  // Privacy options (DataClaw's --redact, --redact-usernames, --no-thinking). Deliberately NOT saved
  // in the browser: a list of a visitor's own names is itself private.
  const [usernames, setUsernames] = useState("");
  const [custom, setCustom] = useState("");
  const [includeThinking, setIncludeThinking] = useState(true);
  const [includeTools, setIncludeTools] = useState(true);

  const opts: RedactOptions = useMemo(
    () => ({ usernames: splitList(usernames), custom: splitList(custom), includeThinking, includeTools }),
    [usernames, custom, includeThinking, includeTools],
  );

  const parsed = url.trim() ? parseShareLink(url) : null;

  /* The askFinz button opens this page as /data-share#import and posts the conversation it read on
     the provider's page. We tell the opener we are ready (repeatedly, the button may still be reading),
     and accept ONE message from it, from a provider origin, that parses as that provider's chat. */
  const readLinkRef = useRef<(link: string) => Promise<void>>(async () => {});
  const [buttonOrigin, setButtonOrigin] = useState("");
  useEffect(() => {
    // The draggable button needs this page's own origin baked in (askfinz.com, or a local dev server).
    const origin = window.location.origin;
    const t = setTimeout(() => setButtonOrigin(origin), 0);
    if (window.location.hash !== "#import" || !window.opener) return () => clearTimeout(t);
    const opener = window.opener as Window;
    let done = false;
    let tries = 0;
    const ping = setInterval(() => {
      if (done || ++tries > 120) return clearInterval(ping);
      if (tries === 1) {
        setTab("link");
        setBusy(true);
        setProgress("Waiting for the askFinz button to send the conversation…");
      }
      opener.postMessage(READY_MESSAGE, "*"); // carries nothing but the word "ready"
    }, 500);
    const onMessage = (e: MessageEvent) => {
      if (done || e.source !== opener || !(e.origin in BUTTON_ORIGINS)) return;
      done = true;
      clearInterval(ping);
      window.removeEventListener("message", onMessage);
      history.replaceState(null, "", window.location.pathname);
      const r = readButtonMessage(e.origin, e.data);
      setBusy(false);
      setProgress("");
      if (!r.ok) {
        setError(r.reason);
      } else if (r.kind === "link") {
        setUrl(r.url);
        void readLinkRef.current(r.url);
      } else {
        setError("");
        setItems([r.item]);
        setNotes([`Read on the ${PROVIDER_LABEL[r.item.conversation.provider]} page in your browser with the askFinz button.`]);
        setSelected(0);
      }
    };
    window.addEventListener("message", onMessage);
    return () => {
      clearTimeout(t);
      clearInterval(ping);
      window.removeEventListener("message", onMessage);
    };
  }, []);
  const bookmarkRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    // React refuses javascript: URLs in href, so the button's link is set on the element directly.
    if (bookmarkRef.current && buttonOrigin) bookmarkRef.current.setAttribute("href", bookmarkletHref(buttonOrigin));
  }, [buttonOrigin]);

  const reset = () => {
    setError("");
    setExcluded(new Set());
    setDonation({ status: "idle", done: [], refused: [], error: "" });
    setItems([]);
    setNotes([]);
    setSelected(0);
    setFilter("");
    setVerdictFilter("all");
  };

  const submitLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return setError("Paste a share link first.");
    if (parsed && !parsed.ok) return setError(parsed.reason);
    await readLink(url.trim());
  };

  // Shared by the form and by the askFinz button (which may hand over just a link).
  const readLink = async (link: string) => {
    reset();
    setBusy(true);
    setProgress("Fetching the conversation…");
    try {
      const res = await fetch(ENDPOINT, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ url: link }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        setError(data.error || "That didn't work. Try again shortly.");
        return;
      }
      // Re-check here so the result matches the checks in this version of the page.
      const conversation = data.conversation as ShareConversation;
      setItems([{ conversation, analysis: analyseConversation(conversation) }]);
    } catch {
      setError("Couldn't reach askFinz just now. Try again in a moment.");
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  useEffect(() => {
    readLinkRef.current = readLink;
  });

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    reset();
    setBusy(true);
    setProgress(`Opening ${file.name}…`);
    try {
      const result = await readExportFile(file);
      const out: AnalysedConversation[] = [];
      const total = result.conversations.length;
      for (let i = 0; i < total; i++) {
        out.push({ conversation: result.conversations[i], analysis: analyseConversation(result.conversations[i]) });
        if (i % 25 === 0) {
          setProgress(`Checking ${i + 1} of ${total.toLocaleString()}…`);
          await tick();
        }
      }
      const rank: Record<Verdict, number> = { rejected: 0, review: 1, clean: 2 };
      out.sort((a, b) => rank[a.analysis.verdict] - rank[b.analysis.verdict]);
      setItems(out);
      setNotes(result.notes);
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't read that file.");
    } finally {
      setBusy(false);
      setProgress("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  /** The redacted copy of every conversation that is not refused, one JSON object per line. */
  const download = async () => {
    setBusy(true);
    setProgress("Preparing the redacted copy…");
    try {
      const lines: string[] = [];
      let n = 0;
      for (const it of items) {
        if (it.analysis.verdict === "rejected") continue;
        lines.push(JSON.stringify(toShareRecord(redactConversation(it.conversation, opts).conversation, opts)));
        if (++n % 50 === 0) await tick();
      }
      const blob = new Blob([lines.join("\n") + "\n"], { type: "application/x-ndjson" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `askfinz-data-share-${new Date().toISOString().slice(0, 10)}.jsonl`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  const counts = useMemo(() => {
    const c = { clean: 0, review: 0, rejected: 0 };
    for (const it of items) c[it.analysis.verdict]++;
    return c;
  }, [items]);

  /* ── Donating (layer 3: shared_backend/apps/marketing/api/data_share.py) ──
     Only the redacted copy is sent, only for conversations that passed and are ticked. Sent in batches
     (at most 40 conversations or about 3 MB each) so a large export never hits a size limit. */
  const chosen = useMemo(
    () => items.map((it, i) => ({ it, i })).filter(({ it, i }) => it.analysis.verdict !== "rejected" && !excluded.has(i)),
    [items, excluded],
  );

  const donate = async () => {
    if (!consent || !chosen.length) return;
    setDonation({ status: "sending", done: [], refused: [], error: "" });
    setBusy(true);
    const done: Donated[] = [];
    const refused: { title: string; reason: string }[] = [];
    let batch: unknown[] = [];
    let size = 0;
    const send = async () => {
      if (!batch.length) return;
      const res = await fetch(DONATE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ consent: true, consent_version: CONSENT_VERSION, records: batch, page_version: "data-share/1" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Donating didn't work. Try again shortly.");
      done.push(...(data.donations || []));
      refused.push(...(data.refused || []));
      batch = [];
      size = 0;
      setProgress(`Donated ${done.length.toLocaleString()} of ${chosen.length.toLocaleString()}…`);
    };
    try {
      for (const { it } of chosen) {
        const rec = toShareRecord(redactConversation(it.conversation, opts).conversation, opts);
        const bytes = JSON.stringify(rec).length;
        if (bytes > 8 * 1024 * 1024) {
          refused.push({ title: it.conversation.title, reason: "too large (over 8 MB)" });
          continue;
        }
        if (batch.length && (batch.length >= 40 || size + bytes > 3 * 1024 * 1024)) await send();
        batch.push(rec);
        size += bytes;
      }
      await send();
      setDonation({ status: "done", done, refused, error: "" });
    } catch (err) {
      setDonation({ status: done.length ? "done" : "idle", done, refused, error: err instanceof Error ? err.message : "Donating didn't work." });
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return items
      .map((it, i) => ({ it, i }))
      .filter(({ it }) => (verdictFilter === "all" || it.analysis.verdict === verdictFilter) && (!q || it.conversation.title.toLowerCase().includes(q)));
  }, [items, filter, verdictFilter]);

  const many = items.length > 1;
  const current = items[selected];

  return (
    <div className="relative rounded-2xl border hairline-strong bg-card overflow-hidden">
      <div aria-hidden className="absolute inset-0 grid-bg-dense opacity-[0.10]" />
      <div className="relative p-5 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="kicker">Share a conversation</div>
          <div role="tablist" className="inline-flex rounded-full border hairline p-0.5">
            {(
              [
                ["link", "Share link", Link2],
                ["file", "Export file", FileArchive],
              ] as const
            ).map(([k, label, Icon]) => (
              <button
                key={k}
                role="tab"
                aria-selected={tab === k}
                type="button"
                onClick={() => setTab(k)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] transition-colors ${tab === k ? "bg-foreground text-background" : "text-foreground/65 hover:text-foreground"}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {tab === "link" ? (
          <form onSubmit={submitLink} className="mt-5">
            <label htmlFor="share-url" className="sr-only">
              Share link
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 min-w-0">
                <Link2 className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/35" aria-hidden />
                <input
                  id="share-url"
                  type="text"
                  inputMode="url"
                  spellCheck={false}
                  autoComplete="off"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://claude.ai/share/…"
                  disabled={busy}
                  className="w-full rounded-xl border hairline-strong bg-background pl-11 pr-4 py-3.5 font-mono text-[14px] outline-none transition-colors focus:border-[hsl(var(--accent)/0.5)] disabled:opacity-60"
                />
              </div>
              <button
                type="submit"
                disabled={busy || !url.trim()}
                className="group shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-foreground text-background px-6 py-3.5 text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {busy ? "Reading…" : "Read and check"}
                {!busy && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
              </button>
            </div>
            <div className="mt-3 min-h-[20px] text-[13px]">
              {parsed?.ok ? (
                <p className="text-foreground/60">
                  Recognised a <span className="text-foreground/90 font-medium">{PROVIDER_LABEL[parsed.provider]}</span> share link.
                </p>
              ) : parsed && !parsed.ok ? (
                <p className="text-amber-700 dark:text-amber-400">{parsed.reason}</p>
              ) : (
                <p className="text-foreground/55">Public share links from ChatGPT, Claude, Gemini, DeepSeek, Grok, Kimi and Mistral. Perplexity and Z.ai: use the askFinz button.</p>
              )}
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
              {HOW.map(({ p, how, soon }) => (
                <div key={p} className={`rounded-xl border hairline bg-background/50 px-3 py-2.5 ${soon ? "opacity-70" : ""}`}>
                  <div className="flex items-center gap-2">
                    {/* Through /api/favicon, never the visitor's browser to a third party (see FaviconTile). */}
                    <FaviconTile
                      name={PROVIDER_LABEL[p]}
                      domain={PROVIDER_DOMAIN[p]}
                      className="h-5 w-5 shrink-0"
                      imgClassName="h-4 w-4 rounded-[4px] bg-white p-[1px]"
                    />
                    <span className="text-[12px] font-medium">{PROVIDER_LABEL[p]}</span>
                    {soon && (
                      <span className="ml-auto rounded-full border hairline px-1.5 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.12em] text-foreground/50">
                        Soon
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-[12px] leading-snug text-foreground/55">{how}</p>
                </div>
              ))}
            </div>

            {/* The askFinz button: reads a shared chat in the visitor's own browser (lib/data-share/bookmarklet.ts). */}
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-dashed border-[hsl(var(--accent)/0.45)] bg-[hsl(var(--accent)/0.05)] px-4 py-3">
              <a
                ref={bookmarkRef}
                href="#askfinz-button"
                draggable
                onClick={(e) => {
                  e.preventDefault();
                  setButtonHint(true);
                }}
                title="Drag me to your bookmarks bar"
                className="inline-flex shrink-0 cursor-grab items-center gap-1.5 self-start rounded-full bg-[hsl(var(--accent))] px-3.5 py-1.5 text-[12.5px] font-medium text-background shadow-sm active:cursor-grabbing"
              >
                <MousePointerClick className="h-3.5 w-3.5" aria-hidden />
                askFinz Data Share
              </a>
              <p className="text-[12px] leading-snug text-foreground/65">
                <span className="font-medium text-foreground/85">The askFinz button.</span> Drag it to your bookmarks bar, open a shared
                chat from any assistant above and click it. It reads the chat in your own browser, so it works where a link alone can&#39;t.{" "}
                <button
                  type="button"
                  onClick={() => setButtonHint(true)}
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  How?
                </button>
              </p>
            </div>
            <ButtonGuide
              open={buttonHint}
              onClose={closeGuide}
              code={buttonOrigin ? bookmarkletHref(buttonOrigin) : ""}
            />
          </form>
        ) : (
          <div className="mt-5">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                handleFile(e.dataTransfer.files?.[0]);
              }}
              disabled={busy}
              className={`w-full rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
                dragging ? "border-[hsl(var(--accent)/0.6)] bg-[hsl(var(--accent)/0.06)]" : "hairline-strong hover:bg-foreground/[0.03]"
              } disabled:opacity-60`}
            >
              {busy ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-foreground/50" /> : <Upload className="mx-auto h-6 w-6 text-foreground/50" />}
              <div className="mt-3 text-sm font-medium">{busy ? progress || "Reading…" : "Drop your export here, or choose a file"}</div>
              <div className="mt-1 text-[12px] text-foreground/55">ChatGPT, Claude or DeepSeek data export (.zip), conversations.json, a Claude memory export, or a saved ChatGPT or Mistral share page (.html)</div>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".zip,.json,.md,.html,.htm,application/zip,application/json,text/markdown,text/html"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <p className="mt-3 flex items-start gap-1.5 text-[12px] leading-relaxed text-foreground/55">
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground/40" aria-hidden />
              <span>Your file is opened and checked inside this browser tab. It is not uploaded.</span>
            </p>
          </div>
        )}

        {/* Privacy options: apply to every result, live. */}
        <details className="group mt-5 rounded-xl border hairline bg-background/40 px-4 py-3">
          <summary className="flex cursor-pointer list-none items-center gap-2 text-[13px] font-medium">
            <Lock className="h-3.5 w-3.5 text-foreground/50" />
            Privacy options
            <span className="text-[12px] font-normal text-foreground/50">names, words to remove, reasoning and tools</span>
            <ChevronDown className="ml-auto h-3.5 w-3.5 text-foreground/40 transition-transform group-open:rotate-180" />
          </summary>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[12px] text-foreground/65">Your names and usernames</span>
              <input
                value={usernames}
                onChange={(e) => setUsernames(e.target.value)}
                placeholder="e.g. Sam Jones, sjones"
                className="mt-1 w-full rounded-lg border hairline bg-background px-3 py-2 text-[13px] outline-none focus:border-[hsl(var(--accent)/0.5)]"
              />
              <span className="mt-1 block text-[11px] text-foreground/45">Each becomes the same short code everywhere it appears.</span>
            </label>
            <label className="block">
              <span className="text-[12px] text-foreground/65">Always remove these words</span>
              <input
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="e.g. company, client, internal site"
                className="mt-1 w-full rounded-lg border hairline bg-background px-3 py-2 text-[13px] outline-none focus:border-[hsl(var(--accent)/0.5)]"
              />
              <span className="mt-1 block text-[11px] text-foreground/45">Separate with commas. Used on this page only, never saved.</span>
            </label>
          </div>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-[13px]">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={includeThinking} onChange={(e) => setIncludeThinking(e.target.checked)} />
              Include the assistant&#39;s reasoning
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={includeTools} onChange={(e) => setIncludeTools(e.target.checked)} />
              Include tool calls and results
            </label>
          </div>
        </details>

        {/* Always visible, before anything is read: the checks help, they do not guarantee. */}
        <div role="note" className="mt-4 flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/[0.07] px-4 py-3">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
          <p className="text-[12.5px] leading-relaxed text-foreground/75">
            <span className="font-medium text-foreground">Not foolproof.</span> Our checks catch common risks and remove common
            private details, but they can miss things, especially names, other people&#39;s details and anything unusual. Always
            read through what you share before you share it.
          </p>
        </div>

        {busy && tab === "link" && progress && (
          <p className="mt-4 flex items-center gap-2 text-[13px] text-foreground/60">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> {progress}
          </p>
        )}

        {error && (
          <div className="mt-5 flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="text-sm text-foreground/80">{error}</p>
          </div>
        )}

        {items.length > 0 && (
          <div className="mt-6 border-t hairline pt-6">
            {many && (
              <div className="mb-6 space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Stat label="Conversations" value={items.length} />
                  <Stat label="Passed" value={counts.clean} />
                  <Stat label="Need a look" value={counts.review} />
                  <Stat label="Refused" value={counts.rejected} />
                </div>
                {notes.length > 0 && (
                  <ul className="space-y-1 text-[12px] text-foreground/55">
                    {notes.map((n, i) => (
                      <li key={i}>{n}</li>
                    ))}
                  </ul>
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-foreground/35" />
                    <input
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      placeholder="Find a conversation by title"
                      className="w-full rounded-lg border hairline bg-background pl-9 pr-3 py-2 text-[13px] outline-none focus:border-[hsl(var(--accent)/0.5)]"
                    />
                  </div>
                  <select
                    value={verdictFilter}
                    onChange={(e) => setVerdictFilter(e.target.value as Verdict | "all")}
                    className="rounded-lg border hairline bg-background px-3 py-2 text-[13px]"
                  >
                    <option value="all">All results</option>
                    <option value="clean">Passed</option>
                    <option value="review">Need a look</option>
                    <option value="rejected">Refused</option>
                  </select>
                </div>
                <ul className="max-h-[280px] overflow-y-auto rounded-xl border hairline divide-y divide-foreground/10">
                  {visible.slice(0, LIST_CAP).map(({ it, i }) => (
                    <li key={i} className="flex items-center">
                      <label className="flex shrink-0 items-center self-stretch pl-3 pr-1" title={it.analysis.verdict === "rejected" ? "Refused conversations can't be donated" : "Include in the donation"}>
                        <input
                          type="checkbox"
                          aria-label={`Include ${it.conversation.title}`}
                          checked={it.analysis.verdict !== "rejected" && !excluded.has(i)}
                          disabled={it.analysis.verdict === "rejected" || donation.status === "sending"}
                          onChange={(e) => {
                            const next = new Set(excluded);
                            if (e.target.checked) next.delete(i);
                            else next.add(i);
                            setExcluded(next);
                          }}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => setSelected(i)}
                        className={`flex min-w-0 flex-1 items-center gap-3 px-3 py-2 text-left transition-colors ${i === selected ? "bg-foreground/[0.06]" : "hover:bg-foreground/[0.03]"}`}
                      >
                        <span className="min-w-0 flex-1 truncate text-[13px]">{it.conversation.title}</span>
                        <span className="hidden sm:inline font-mono text-[11px] text-foreground/45">{it.analysis.stats.words.toLocaleString()} words</span>
                        <VerdictPill v={it.analysis.verdict} small />
                      </button>
                    </li>
                  ))}
                  {visible.length > LIST_CAP && (
                    <li className="px-3 py-2 text-[12px] text-foreground/55">
                      Showing {LIST_CAP} of {visible.length.toLocaleString()}. Narrow it down with the search box.
                    </li>
                  )}
                  {visible.length === 0 && <li className="px-3 py-3 text-[12px] text-foreground/55">No matches.</li>}
                </ul>
              </div>
            )}

            {current && <ConversationView key={selected} item={current} opts={opts} />}

            <DonatePanel
              chosen={chosen.length}
              total={items.length}
              consent={consent}
              setConsent={setConsent}
              donation={donation}
              busy={busy}
              onDonate={donate}
              onDownload={download}
            />
          </div>
        )}
      </div>
    </div>
  );
}
